import reactUpdate from 'immutability-helper';

import FolderDAO from 'file-browser/FolderDAO';
import {BulkAssemblyPreferences} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import preferences from 'Preferences';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {WorklistSummary} from 'worklists/WorklistConstants';

import {
  Assembly,
  AssemblyWithOptionalMeta,
  DataSource,
  EndpointType,
  HomologyDetectionOptions,
  HomologyDetectionResponse,
  LocalAssembly,
  Slot,
  Summary,
} from './BulkAssemblyConstants';

async function convertToJSON(assembly: Assembly): Promise<AssemblyWithOptionalMeta> {
  const sequenceIdsPromises = assembly.dataSources.map(async (dataSource) => {
    const folderIds = dataSource.fileIds!.filter((id) => APIUtil.isFolder(id));
    const seqIds = dataSource.fileIds!.filter((id) => !APIUtil.isFolder(id));
    const nestedSeqIds = await FolderDAO.getNestedDnaSequencesAndOligos(folderIds);
    return [...seqIds, ...nestedSeqIds.sequenceIds];
  });

  const sequenceIdsPerDataSource = await Promise.all(sequenceIdsPromises);

  const gibsonParameters = preferences.get(BulkAssemblyPreferences.GIBSON_PARAMETERS);

  return reactUpdate(assembly as AssemblyWithOptionalMeta, {
    slots: {
      $apply: (slots: Array<Slot>) =>
        slots.map((slot) => ({
          ...slot,
          slotParams: {
            ...slot.slotParams,
            endpoints: slot.slotParams.endpoints.map((endpoint) =>
              endpoint.type === EndpointType.GIBSON
                ? {
                    ...endpoint,
                    gibsonOptions: gibsonParameters,
                  }
                : endpoint
            ),
          },
        })),
    },
    dataSources: {
      $apply: (dataSources: Array<DataSource>) =>
        dataSources.map((dataSource, i) => ({
          id: dataSource.id,
          sequenceIds: sequenceIdsPerDataSource[i],
        })),
    },
    meta: {$set: undefined},
  });
}

export async function validate(assembly: LocalAssembly): Promise<Summary> {
  const assemblyJSON = await convertToJSON(assembly);

  return net.postJSONPromise({
    url: '/1/api/bulk-workflows/assembly/validate',
    data: {assembly: assemblyJSON},
  });
}

export async function detectHomology(
  assembly: LocalAssembly,
  options: HomologyDetectionOptions
): Promise<HomologyDetectionResponse> {
  const assemblyJSON = await convertToJSON(assembly);

  return TaskRunner.postJSONPromise({
    url: '/1/api/bulk-workflows/assembly:detect-homology',
    data: {assembly: assemblyJSON, options},
  });
}

export async function runAssembly(
  assembly: LocalAssembly
): Promise<{worklistSummary?: WorklistSummary; primerWorklistSummary?: WorklistSummary}> {
  const assemblyJSON = await convertToJSON(assembly);

  return TaskRunner.postJSONPromise({
    url: '/1/api/bulk-workflows/assembly/run',
    data: {assembly: assemblyJSON},
  });
}
